/**
 * Machine details page component.
 *
 * @category pages
 * @module machine-details
 */

import { type ReactElement, type ReactNode, useState } from "react";
import { t } from "@lingui/core/macro";
import { keepPreviousData } from "@tanstack/react-query";
import { Temporal } from "temporal-polyfill";

import { machineRoutes, machineUrls } from "_/routes";

import { Button } from "_/components/button";
import { DateTime } from "_/components/date-time";
import { JobsListing } from "_/components/jobs-listing";
import { MachineEvents } from "_/components/machine-events";
import { MachineHeader } from "_/components/machine-header";
import { MachineTemperature } from "_/components/machine-temperature";
import { Route, Switch } from "_/components/router";
import { Tooltip } from "_/components/tooltip";
import { MachineLogs } from "_/components/machine-logs";

import {
  type Machine,
  type MachineImage,
  useMachineImages,
  useMachineThermals,
} from "_/data/machines";
import { imageURL, thumbURL } from "_/data/images";
import { useIsAonUser } from "_/data/users";
import { useCurrentOrg } from "_/data/orgs";

import { formatHourMinuteDuration } from "_/utils/datetime";

import { MachineConfiguration } from "./machine-configuration";

import * as S from "./styled";

export type MachineDetailsProps = {
  machine: Machine;
};

export const LastMachineImage = ({ machine }: MachineDetailsProps) => {
  const { data: currentOrg } = useCurrentOrg();
  const [displayedImage, setDisplayedImage] = useState<MachineImage>();

  // Show images from a week before we last saw the machine
  // Note that these number values are in microseconds.
  const lastSeen = machine.lastSeen
    ? Temporal.Instant.from(machine.lastSeen)
    : undefined;
  const from = lastSeen
    ? lastSeen.subtract({ hours: 7 * 24 }).epochMilliseconds * 1000
    : undefined;
  const to = lastSeen
    ? lastSeen.add({ minutes: 10 }).epochMilliseconds * 1000
    : undefined;

  const { data: images, isSuccess } = useMachineImages(
    {
      machineId: machine.id,
      from,
      to,
      limit: 50,
    },
    { placeholderData: keepPreviousData }
  );

  const lastChamberImage = images?.find(
    (machineImage) => machineImage.camera === "chamber_cam_0"
  );
  const src = lastChamberImage
    ? imageURL(lastChamberImage.image.id)
    : undefined;
  const thumb = lastChamberImage
    ? thumbURL(lastChamberImage.image.id)
    : undefined;
  const imageUnavailable =
    machine.orgId !== currentOrg.id || (isSuccess && !lastChamberImage);

  const handleThumbnailLoad = (loadedSrc: string) => {
    if (lastChamberImage && (loadedSrc === src || loadedSrc === thumb)) {
      setDisplayedImage(lastChamberImage);
    }
  };

  if (imageUnavailable)
    return (
      <S.LastMachineImageContainer>
        <S.UnavailableImageMessage>{t`common.no-image-available`}</S.UnavailableImageMessage>
      </S.LastMachineImageContainer>
    );

  if (!src)
    return (
      <S.LastMachineImageContainer>
        <S.UnavailableImageMessage />
      </S.LastMachineImageContainer>
    );

  return (
    <S.LastMachineImageContainer>
      <S.Image src={src} thumb={thumb} onThumbnailLoad={handleThumbnailLoad} />
      <S.ImageTimestamp>
        {displayedImage && (
          <DateTime value={new Date(displayedImage.capturedAt / 1_000)} />
        )}
      </S.ImageTimestamp>
    </S.LastMachineImageContainer>
  );
};

const NewHardwareSetupWidget = ({ machine }: MachineDetailsProps) => {
  const isAonUser = useIsAonUser();

  // Only show the machine setup content to Aon3D users and for machines that have never connected.
  if (machine.lastSeen || !isAonUser) return null;

  const { hardware } = machine;
  const commandLine = `echo '{"hardwareId":"${hardware.id}", "serial":"${hardware.serial}"}' > /home/aon/printer_data/platform-agent/identity.json && sudo chattr +i /home/aon/printer_data/platform-agent/identity.json`;

  return (
    <S.Widget>
      <S.WidgetTitleContainer>
        <h2>{t`common.new-hardware-setup`}</h2>
      </S.WidgetTitleContainer>
      <div>
        {t`common.new-hardware-setup-help`}
        <S.CopyableCode>{commandLine}</S.CopyableCode>
      </div>
    </S.Widget>
  );
};

const TEMPERATURE_DELAY_LIMIT = Temporal.Duration.from({ seconds: 20 });

export const TemperatureDelayAlert = ({ machine }: MachineDetailsProps) => {
  const isAonUser = useIsAonUser();
  const thermalsQuery = useMachineThermals(machine);

  const latestTimestamp = thermalsQuery.data?.timestamp.at(-1);

  if (!machine.lastSeen || latestTimestamp === undefined) return null;

  // For now, internal-only visibility.
  if (!isAonUser) return null;

  // Thermal event timestamps are in microseconds.
  const delay = Temporal.Instant.fromEpochMilliseconds(
    Math.trunc(latestTimestamp / 1000)
  ).until(Temporal.Instant.from(machine.lastSeen));

  if (Temporal.Duration.compare(delay, TEMPERATURE_DELAY_LIMIT) <= 0) {
    return null;
  }

  // If printing, temp variations are essentially guaranteed to happen. But
  // during non-printing times it's theoretically possible for the temp sensors
  // to report exactly the same value for a long period, which will prevent
  // Agent from sending updated temp events.
  // TODO: look for the last stored event regardless of type.
  if (machine.status !== "printing") return null;

  const duration = formatHourMinuteDuration(delay);
  const message = t`components.machine-details.event-processing-delayed ${duration}`;

  return (
    <Tooltip
      content={message}
      placement="top"
      target={
        <>
          <S.WarningIcon variant="ClockAlert" aria-label={message} />
          <S.TemperatureHeadingSubtext>{duration}</S.TemperatureHeadingSubtext>
        </>
      }
    />
  );
};

const MachineOverview = ({ machine }: MachineDetailsProps): ReactNode => {
  return (
    <S.MachineOverviewContainer>
      <S.NestedWidgetContainer>
        <S.UseRemainingWidth>
          <S.Widget>
            <S.WidgetTitleContainer>
              <S.TemperatureHeading>
                {t`common.current-temps`}
                <TemperatureDelayAlert machine={machine} />
              </S.TemperatureHeading>
              <S.Link to={machineUrls.thermals(machine.id)}>
                <Button size="small">{t`common.thermal-history`}</Button>
              </S.Link>
            </S.WidgetTitleContainer>
            <S.MachineTemperatureDisplay machine={machine} view="wide" />
          </S.Widget>
        </S.UseRemainingWidth>
        <S.Widget>
          <S.WidgetTitleContainer>
            <h2>{t`common.last-image-captured`}</h2>
          </S.WidgetTitleContainer>
          <S.MachineOverviewInfo>
            <LastMachineImage machine={machine} />
          </S.MachineOverviewInfo>
        </S.Widget>
      </S.NestedWidgetContainer>
      <S.Widget>
        <S.WidgetTitleContainer>
          <h2>{t`common.jobs`}</h2>
        </S.WidgetTitleContainer>
        <JobsListing limit={50} machine={machine} />
      </S.Widget>
      <NewHardwareSetupWidget machine={machine} />
    </S.MachineOverviewContainer>
  );
};

export const MachineDetails = ({
  machine,
}: MachineDetailsProps): ReactElement => {
  return (
    <S.Wrapper>
      <MachineHeader machine={machine} />
      <Switch>
        <Route path={machineRoutes.index}>
          <MachineOverview machine={machine} />
        </Route>
        <Route path={machineRoutes.thermals}>
          <MachineTemperature machine={machine} />
        </Route>
        <Route path={machineRoutes.configuration}>
          <MachineConfiguration machine={machine} />
        </Route>
        <Route path={machineRoutes.events}>
          <MachineEvents machine={machine} />
        </Route>
        <Route path={machineRoutes.logs}>
          <MachineLogs machine={machine} />
        </Route>
      </Switch>
    </S.Wrapper>
  );
};
